import {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {SEARCH_ROUTE} from 'constants/index'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {
  getSearchParameters,
  getSearchParametersForSearchBox
} from 'modules/sapiSearch/selectors'
import {SearchUrlParams} from 'modules/search/types'
import {transformSapiFiltersToFiltersURL} from 'utils/filters'
import url from 'utils/url'

import {decorateUrlWithSubBrand} from '@vio/core/src/_web/brand/utils'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {navigateTo} from '@vio/core/src/_web/utils/browser/navigateTo'
import {parseQueryString} from '@vio/core/src/utils/url'

import {ensureBooleanUrlParams} from './ensureBooleanUrlParams'

type UpdateMethod = 'navigate' | 'push' | 'replace' | 'open'

export type UpdateLocationOptionsType = {
  mergeWithCurrentParams?: boolean
  method?: UpdateMethod
  state?: Record<string, unknown>
  basePath?: string
  shallow?: boolean
}

export type UpdateLocationCallbackType = (
  params: SearchUrlParams,
  options?: UpdateLocationOptionsType
) => void | Promise<boolean>

interface UrlUpdateFunctions {
  changeLocation: UpdateLocationCallbackType
  navigateWithParameters: UpdateLocationCallbackType
  updateLocation: UpdateLocationCallbackType
  updateLocationShallow: UpdateLocationCallbackType
}

/**
 * Generate a URL for navigation with the given parameters
 * @param params - The search parameters to include in the URL
 * @param options - Options for the URL generation including **basePath** (default: SEARCH_ROUTE) and **mergeWithCurrentParams** (default: true)
 **/
export const generateNavigationUrl = (
  params: SearchUrlParams,
  options?: {
    basePath?: string
    mergeWithCurrentParams?: boolean
  }
) => {
  const {basePath = SEARCH_ROUTE, mergeWithCurrentParams = true} = options ?? {}
  const searchParams =
    mergeWithCurrentParams && typeof window !== 'undefined'
      ? parseQueryString(window.location.search)
      : {}
  const mergedParams: SearchUrlParams = {
    ...searchParams,
    ...ensureBooleanUrlParams(params)
  }
  const path = url.stringify(mergedParams)

  const finalUrl = path ? `${basePath}?${path}` : basePath
  // Decorate URL with subBrand if present in current URL
  return decorateUrlWithSubBrand(finalUrl)
}

export const useUrlUpdater = (): UrlUpdateFunctions => {
  const {replace, push} = useRouterProvider()
  const isSeoSRP = useIsSeoSRP()
  const searchBoxParams = useSelector(getSearchParametersForSearchBox)
  const searchParams = useSelector(getSearchParameters)
  const appliedFilters = transformSapiFiltersToFiltersURL(searchParams)

  /**
   * Update current location while preserving existing search params
   */
  const performLocationUpdate = useCallback(
    (
      params: SearchUrlParams,
      {
        basePath = SEARCH_ROUTE,
        mergeWithCurrentParams = true,
        method = 'push',
        shallow = true
      }: UpdateLocationOptionsType = {}
    ): void | Promise<boolean> => {
      const fullPath = generateNavigationUrl(params, {
        basePath,
        mergeWithCurrentParams
      })

      switch (method) {
        case 'navigate':
          navigateTo(fullPath)
          break
        case 'push':
          return push(fullPath, {shallow})
        case 'replace':
          return replace(fullPath, {shallow})
        case 'open':
          window.open(fullPath, '_blank')
          break
      }
    },
    [replace, push]
  )

  const updateLocation = useCallback(
    (params, options) => {
      // When in a Seo SRP, it should always merge essential search parameters(placeId, checkIn, checkOut) into the URL before navigating to the regular SRP.
      if (isSeoSRP) {
        return performLocationUpdate(
          {...searchBoxParams, ...appliedFilters, ...params},
          {
            ...options,
            basePath: SEARCH_ROUTE
          }
        )
      } else {
        return performLocationUpdate(params, options)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSeoSRP, performLocationUpdate]
  )

  /**
   * While preserving existing search params, navigate to new page
   */
  const navigateWithParameters = useCallback(
    (
      params: SearchUrlParams,
      options: UpdateLocationOptionsType = {}
    ): void | Promise<boolean> => {
      return updateLocation(params, {
        ...options,
        method: 'navigate'
      })
    },
    [updateLocation]
  )

  /**
   * Fully change location
   */
  const changeLocation = useCallback(
    (
      params: SearchUrlParams,
      options: UpdateLocationOptionsType = {}
    ): void | Promise<boolean> => {
      return updateLocation(params, {
        ...options,
        mergeWithCurrentParams: false
      })
    },
    [updateLocation]
  )

  /**
   * Prevents data refetching on back navigation by pushing the current URL shallowly before navigating to the new URL (also shallowly).
   */
  const updateLocationShallow = useCallback(
    (
      params: SearchUrlParams,
      options: UpdateLocationOptionsType = {}
    ): void => {
      performLocationUpdate(
        {},
        {
          ...options,
          method: 'replace',
          mergeWithCurrentParams: true, // This will preserve the current URL params
          shallow: true
        }
      )
      performLocationUpdate(params, {...options, shallow: true})
    },
    [performLocationUpdate]
  )

  return useMemo(
    () => ({
      updateLocation,
      updateLocationShallow,
      navigateWithParameters,
      changeLocation
    }),
    [
      updateLocation,
      navigateWithParameters,
      changeLocation,
      updateLocationShallow
    ]
  )
}
